import { NavLink } from 'react-router-dom'
import { Compass, LayoutGrid, LogOut, Settings } from 'lucide-react'
import type { NavItem } from '../../app/navConfig'
import { useAuth } from '../../features/auth/useAuth'
import { cn } from '../lib/cn'

export function Sidebar({
  tripItems,
  tripTitle,
}: {
  tripItems: NavItem[]
  tripTitle?: string
}) {
  const { user, logout } = useAuth()

  return (
    <aside className="hidden w-64 shrink-0 flex-col border-r border-st-line bg-st-surface md:flex">
      <div className="flex items-center gap-2 px-5 py-5">
        <span className="flex h-9 w-9 items-center justify-center rounded-st-md bg-st-primary text-white">
          <Compass className="h-5 w-5" />
        </span>
        <span className="font-display text-lg font-semibold text-st-text">Smart Travel</span>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
        <SidebarLink item={{ to: '/', label: 'All trips', Icon: LayoutGrid }} end />

        {tripItems.length > 0 && (
          <div className="pt-4">
            <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-st-muted">
              {tripTitle ?? 'Trip'}
            </p>
            {tripItems.map((item) => (
              <SidebarLink
                key={item.to}
                item={item}
                end={item.to.split('/').length === 3}
              />
            ))}
          </div>
        )}
      </nav>

      <div className="border-t border-st-line p-3">
        <SidebarLink item={{ to: '/settings', label: 'Settings', Icon: Settings }} />
        <div className="mt-1 flex items-center gap-3 px-2 py-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-st-full bg-st-surface-2 text-sm font-semibold text-st-primary">
            {user?.name?.charAt(0) ?? '?'}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-st-text">{user?.name ?? 'Guest'}</p>
            <p className="truncate text-xs text-st-muted">{user?.email}</p>
          </div>
          <button
            type="button"
            onClick={() => void logout()}
            className="rounded-st-sm p-2 text-st-muted hover:bg-st-surface-2 hover:text-st-danger"
            title="Sign out"
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  )
}

function SidebarLink({ item, end }: { item: NavItem; end?: boolean }) {
  const { Icon, label, to } = item
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-st-md px-3 py-2 text-sm font-medium transition',
          isActive
            ? 'bg-st-primary/10 text-st-primary'
            : 'text-st-muted hover:bg-st-surface-2 hover:text-st-text',
        )
      }
    >
      <Icon className="h-4.5 w-4.5" />
      {label}
    </NavLink>
  )
}
